<template>
  <div
    class="
      w-100
      flex-grow-1
      d-flex
      flex-column
      align-items-stretch align-items-lg-end
      justify-content-end
      mb-3
    "
  >
    <!-- set canvas size based on parent container -->
    <canvas
      id="canvas"
      ref="threejs-canvas"
      class="top-0 start-0 end-0 bottom-0 h-100 w-100 position-absolute"
    />
    <!-- SVG Crosshair Overlay -->
    <img
      src="@/assets/crosshairs.svg"
      class="crosshair-overlay w-100 h-100 position-absolute bottom-0 start-0"
      :class="{ 'd-none': status !== STATUS.FINE_ALIGNMENT }"
      alt="crosshair"
    />
    <MobileButtonPanel
      @z-key="toggleZoom"
      @x-key="invertSight"
      @f-key="updateIntersections"
      @zero-key="zeroAzimuthAngle"
      @two-key="zeroAzimuthAngleAt180Minus2A"
      @p-key="pKeyAction"
      :keyDown="mobileKeyDown"
      :keyUp="mobileKeyUp"
      :disableButtons="disableInput"
      style="z-index: 1"
      v-if="isMobile()"
    />
    <DataOutput
      ref="dataOutput"
      :V="verticalAngle"
      :hz="horizontalAngle"
      :intersectHypotenuse="intersectHypotenuse"
      :isInverted="invertForDatatable"
    />
  </div>
</template>

<script>
import * as THREE from "three";
import KeyboardState from "@/helpers/KeyboardState.js";
import CameraControls from "camera-controls";
import {
  STATUS,
  DIRECTION,
  TRIPOD_HEIGHT,
  SIGHT_HEIGHT,
  POLE_HEIGHT,
  DEGREES_PER_SECOND,
  KEYS
} from "@/helpers/constants.js";
import { PointName, CurveConstants } from "@/data/content.js";
import { GroundProjectedEnv } from "@/components/activity/GroundProjectedEnv.js";
// import { GroundedSkybox } from "three/examples/jsm/objects/GroundedSkybox.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import gsap from "gsap";
import DataOutput from "./DataOutput";
import MobileButtonPanel from "./MobileButtonPanel";
import { mapGetters, mapState, mapActions } from "vuex";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import spiralCurveSceneMixin from "@/mixins/spiralCurveSceneMixin";
import debuggingControlsMixin from "@/mixins/debuggingControlsMixin";
import BrowserDetection from "@/mixins/browserDetection";

import {
  setCastShadow,
  configureLightShadowSettings,
  configureRendererShadowSettings,
  createLabelDiv
} from "@/helpers/ThreeHelpers";
import {
  CSS2DRenderer,
  CSS2DObject
} from "three/examples/jsm/renderers/CSS2DRenderer.js";

const models = {};
let scene;
let camera;
let controls;
const intersectionTargets = [];
let raycaster;
const postprocessing = {};
const totalStation = {};
let animationID;
// Camera control properties
let defaultDamping, defaultAzimuthRotateSpeed, defaultPolarRotateSpeed;
const environmentMaps = {
  [PointName.PI]: null,
  [PointName.TS]: null,
  [PointName.ST]: null,
  [PointName.SC]: null
};

const PRISM_MOVE_SPEED = 0.01;
const PRECISE_PRISM_MOVE_SPEED = PRISM_MOVE_SPEED * 0.1;

export default {
  name: "SurveyingScene",
  components: {
    DataOutput,
    MobileButtonPanel
  },
  emits: ["modelsLoaded"],
  mixins: [spiralCurveSceneMixin, debuggingControlsMixin, BrowserDetection],
  data: function () {
    // Non-reactive data
    scene = this.scene = new THREE.Scene();
    // Reactive data
    return {
      horizontalAngle: 0,
      horizontalAngleNoise: 0,
      verticalAngle: 0,
      floorHeight: 0,
      cameraDefaultPosition: new THREE.Vector3(0, SIGHT_HEIGHT, 0.75),
      cameraDebugPosition: new THREE.Vector3(0, 1.95, 12),
      coarseAngleAmount: Math.PI / 180,
      fineAngleAmount: Math.PI / (360 * 32),
      coarseZoom: 1,
      fineZoom: 12,
      status: STATUS.COARSE_ALIGNMENT,
      STATUS,
      envParams: {
        [PointName.PI]: {
          radius: 6000,
          height: 1.3,
          rotation: -2.58867234655799,
          debugControlsActive: true,
          brightness: 1.02,
          initialAzimuthAngle: 2,
          maxAzimuthAngle: Infinity,
          minAzimuthAngle: -Infinity
        },
        [PointName.TS]: {
          radius: 626,
          height: 1.45,
          rotation: -2.89654842660979,
          debugControlsActive: true,
          brightness: 0.908,
          initialAzimuthAngle: -1.5,
          maxAzimuthAngle: -1,
          minAzimuthAngle: -2.17
        },
        // TODO: these settings should be tweaked for the actual environment maps
        [PointName.SC]: {
          radius: 315,
          height: 1.55,
          rotation: -0.9,
          debugControlsActive: true,
          brightness: 1.144,
          initialAzimuthAngle: 2,
          maxAzimuthAngle: Infinity,
          minAzimuthAngle: -Infinity
        },
        [PointName.ST]: {
          radius: 6000,
          height: 2.8,
          rotation: -1.40115032350105,
          debugControlsActive: true,
          brightness: 1.02,
          initialAzimuthAngle: 1.5,
          maxAzimuthAngle: 2,
          minAzimuthAngle: 0.9
        }
      },
      telescopeInverted: false,
      invertForDatatable: false,
      intersected: null,
      targets: [],
      azimuthAngleZero: Math.random() * Math.PI * 2,
      loadingModels: true,
      labelRenderer: null,
      // Used to track which keys are being held down in the mobile button panel
      mobileKeys: []
    };
  },
  created: function () {
    this.DIRECTION = DIRECTION;
  },
  mounted: function () {
    // Go back to home page if no points selected
    if (!this.pointDataExists) {
      this.$router.push({ name: "Main" });
    } else {
      // Register Event Listeners
      window.addEventListener("resize", this.updateDimensions);
      this.init();
    }
  },
  unmounted: function () {
    window.removeEventListener("resize", this.updateDimensions);
    // Fixes broken keyboard controls on live reload
    this.keyboard?.dispose();
    // Attempt to fix slow performance on live reload
    this.renderer?.forceContextLoss();
    // Remove keyboard event listeners
    // controls.dispose();
    // end animation loop
    window.cancelAnimationFrame(animationID);
    // Remove label renderer
    if (this.labelRenderer) {
      this.labelRenderer.domElement.remove();
    }
  },
  computed: {
    ...mapGetters([
      // "getFromPointLocation",
      // "getToPointLocation",
      "getAtPointName",
      "getPointPositions",
      "isPanoramaDebugMode",
      "pointPositions",
      "getHasMeasuredOnCurrentStep"
    ]),
    ...mapState("datastore", ["entryLoaded"]),
    pointDataExists: function () {
      return this.getAtPointName;
      // && this.getFromPointLocation &&
      // this.getToPointLocation
    },
    disableInput: function () {
      return this.status == STATUS.ROTATING_TO_INVERSE;
    },
    // Returns the distance of the intersected object from the origin, in meters.
    // -1 if no intersection
    intersectHypotenuse: function () {
      if (this.intersected) {
        const bAngle = Math.PI / 2 - controls.polarAngle;
        // Subtract camera-origin distance from intersect distance
        const cameraToOrigin = (1 / Math.cos(bAngle)) * controls.distance;
        return this.intersected.distance - cameraToOrigin;
      }
      return -1;
    }
  },
  methods: {
    ...mapActions(["setHasMeasuredOnCurrentStep", "setErrorMessage"]),
    mobileKeyDown: function (key) {
      this.mobileKeys.push(key);
    },
    mobileKeyUp: function (key) {
      this.mobileKeys = this.mobileKeys.filter((k) => k !== key);
    },
    zeroAzimuthAngle: function () {
      this.azimuthAngleZero = controls.azimuthAngle;
      // Store the azimuth angle in localStorage
      localStorage.setItem(
        "azimuthAngleZero",
        this.azimuthAngleZero.toString()
      );
    },
    zeroAzimuthAngleAt180Minus2A: function () {
      this.azimuthAngleZero =
        controls.azimuthAngle + Math.PI - 2 * CurveConstants.A;
      // Store the azimuth angle in localStorage
      localStorage.setItem(
        "azimuthAngleZero",
        this.azimuthAngleZero.toString()
      );
    },
    getGLTFModelPath(name) {
      return require(`@/assets/models/${name}.gltf`);
    },
    // GLB models are stored in the public folder due to issues with webpack
    getGLBModelPath(name) {
      return `models/${name}.glb`;
    },
    invertSight: function () {
      // Lock the camera mouse controls
      controls.enabled = false;
      const prevStatus = this.status;
      this.status = STATUS.ROTATING_TO_INVERSE;
      const verticalDestination = totalStation.armature.rotation.z + Math.PI;
      const horizontalDestination =
        models["total station"].rotation.y + Math.PI;
      gsap
        .timeline({
          onComplete: () => {
            this.status = prevStatus;
            this.telescopeInverted = !this.telescopeInverted;
            // Enable controls
            controls.enabled = true;
          }
        })
        .to(totalStation.armature.rotation, {
          z: verticalDestination,
          duration: 4
        });
      gsap.to(models["total station"].rotation, {
        y: horizontalDestination,
        duration: 4
      });
    },
    updatePitch: function () {
      const angleCoefficient = this.telescopeInverted ? -1 : 1;
      // Mod by 2 PI to prevent giant radian values after multiple inversions
      const adjustedControlAngle =
        (angleCoefficient * controls.polarAngle - 3 * (Math.PI / 2)) %
        (2 * Math.PI);
      // If not telescope isn't automatically rotating and if control angle does not match armature angle
      if (
        this.status != STATUS.ROTATING_TO_INVERSE &&
        totalStation.armature.rotation.z % Math.PI !=
          adjustedControlAngle % Math.PI
      ) {
        totalStation.armature.rotation.z = adjustedControlAngle;
      }
    },
    initCameraControls: function () {
      controls.maxDistance = this.cameraDefaultPosition.z;
      controls.maxPolarAngle = Math.PI * 0.8;
      controls.distance = this.cameraDefaultPosition.z;
      controls.minDistance = this.cameraDefaultPosition.z;
      // disable panning with mouse right-click
      controls.mouseButtons.right = CameraControls.ACTION.NONE;

      // Reduce mouse sensitivity for zoomed-out state
      controls.azimuthRotateSpeed = 0.5;
      controls.polarRotateSpeed = 0.5;
    },
    // Update camera position based on "At" position
    updateCameraPosition: function () {
      const atPointPosition = this.getPointPositions[this.getAtPointName];

      controls.setPosition(
        atPointPosition.x,
        this.cameraDefaultPosition.y,
        // offset camera position by distance person stands behind the total station
        atPointPosition.z + this.cameraDefaultPosition.z
      );
      camera.position.set(
        atPointPosition.x,
        this.cameraDefaultPosition.y,
        // offset camera position by distance person stands behind the total station
        atPointPosition.z + this.cameraDefaultPosition.z
      );
      // set camera target to the total station's top viewfinder
      controls.setTarget(
        atPointPosition.x,
        this.cameraDefaultPosition.y,
        atPointPosition.z
      );
      // set camera azimuth angle limits
      controls.maxAzimuthAngle =
        this.envParams[this.getAtPointName].maxAzimuthAngle;
      controls.minAzimuthAngle =
        this.envParams[this.getAtPointName].minAzimuthAngle;
      // set camera azimuth angle to initial azimuth angle
      controls.azimuthAngle =
        this.envParams[this.getAtPointName].initialAzimuthAngle;

      // update total station components' position
      models["total station"].position.set(
        atPointPosition.x,
        TRIPOD_HEIGHT,
        atPointPosition.z
      );
      models["tribrach"].position.set(
        atPointPosition.x,
        TRIPOD_HEIGHT - 0.015,
        atPointPosition.z
      );
      models["tripod"].position.set(
        atPointPosition.x,
        this.floorHeight,
        atPointPosition.z
      );

      // reticle border adjustment
      models["reticle"].position.set(
        atPointPosition.x,
        this.cameraDefaultPosition.y,
        atPointPosition.z
      );
      // reticle should always face the camera
      models["reticle"].lookAt(camera.position);
    },
    toggleControls: function () {
      if (this.debugControlsActive) {
        this.initCameraControls();
      } else {
        controls.maxDistance = this.cameraDebugPosition.z;
        controls.distance = this.cameraDebugPosition.z;
      }
      controls.update();
      this.debugControlsActive = !this.debugControlsActive;
    },
    toggleZoom: function () {
      // Toggle Zoom
      if (this.status == STATUS.FINE_ALIGNMENT) {
        // Reset camera rotate speed and damping
        controls.azimuthRotateSpeed = defaultAzimuthRotateSpeed;
        controls.polarRotateSpeed = defaultPolarRotateSpeed;
        controls.dampingFactor = defaultDamping;

        controls.zoomTo(this.coarseZoom, true);
        models["tripod"].visible = true;
        models["total station"].visible = true;
        models["reticle"].visible = false;
        this.status = STATUS.COARSE_ALIGNMENT;
        // Un-comment if using atmosphere shader
        // This is a functioning example of post-processing
        // postprocessing.composer.removePass(atmospherePass);
      } else if (this.status == STATUS.COARSE_ALIGNMENT) {
        // Decrease camera rotate speed and increase damping
        defaultDamping = controls.dampingFactor;
        defaultAzimuthRotateSpeed = controls.azimuthRotateSpeed;
        defaultPolarRotateSpeed = controls.polarRotateSpeed;
        controls.polarRotateSpeed = 0.01;
        controls.azimuthRotateSpeed = 0.01;
        controls.dampingFactor = 0.1;

        controls.zoomTo(this.fineZoom, true);
        // Make happen after zoomTo transition completed, or fade out visibility
        models["tripod"].visible = false;
        models["reticle"].visible = true;
        models["total station"].visible = false;
        this.status = STATUS.FINE_ALIGNMENT;
        // Un-comment if using atmosphere shader
        // This is a functioning example of post-processing
        // postprocessing.composer.addPass(atmospherePass);
      }
    },
    /**
     * Move the camera in a given direction
     * @param {string} direction - The direction to move the camera
     * @param {boolean} isPrecise - Whether the movement has the precision modifier added
     */
    moveCamera: function (direction, isPrecise = false) {
      let offset =
        this.status === STATUS.FINE_ALIGNMENT
          ? this.fineAngleAmount
          : this.coarseAngleAmount;
      if (isPrecise) {
        offset *= 0.04;
      }

      switch (direction) {
        case DIRECTION.UP:
          controls.rotatePolarTo(controls.polarAngle + offset, true);
          break;
        case DIRECTION.DOWN:
          controls.rotatePolarTo(controls.polarAngle - offset, true);
          break;
        case DIRECTION.LEFT:
          controls.rotateAzimuthTo(controls.azimuthAngle + offset, true);
          break;
        case DIRECTION.RIGHT:
          controls.rotateAzimuthTo(controls.azimuthAngle - offset, true);
          break;
      }
    },
    updateIntersections: function () {
      // Update Raycaster to check center of screen - https://discourse.threejs.org/t/raycaster-do-not-stays-in-center-when-i-am-updating-camera/20013/3
      const dir = camera.getWorldDirection(new THREE.Vector3());
      const pos = camera.getWorldPosition(new THREE.Vector3());
      raycaster.set(pos, dir.normalize());

      // Check for intersection with target
      const intersects = raycaster.intersectObjects(intersectionTargets, true);

      if (intersects.length > 0) {
        // May want to check for a specific part of the target here
        const targetIntersect = intersects[0];
        if (
          !this.intersected ||
          this.intersected.distance !== targetIntersect.distance
        ) {
          this.intersected = targetIntersect;
        }
      } else {
        this.intersected = null;
      }

      // Datatable only updates to inverted angle display when laser is fired
      this.invertForDatatable = this.telescopeInverted;
      this.setHasMeasuredOnCurrentStep(true);
    },
    animate: function () {
      const delta = this.clock.getDelta();
      const isShiftPressed =
        this.mobileKeys.includes(KEYS.SHIFT) || this.keyboard.pressed("shift");
      // Prism movement variables
      const prismMoveSpeed = isShiftPressed
        ? PRECISE_PRISM_MOVE_SPEED
        : PRISM_MOVE_SPEED;
      let direction = new THREE.Vector3();
      const getCameraHorizontalDirection = (vector) => {
        camera.getWorldDirection(vector);
        vector.y = 0; // Nullify the y-component to restrict movement to x-z plane
        vector.normalize(); // Normalize the direction vector after modification
      };

      // Update Env
      // this.environmentMap.radius = this.envParams.radius;
      // this.environmentMap.height = this.envParams.height;

      // Keyboard Controls
      controls.update(delta);
      this.keyboard.update();
      if (this.status != STATUS.ROTATING_TO_INVERSE) {
        if (this.keyboard.pressed("A") || this.mobileKeys.includes(KEYS.A)) {
          // move left
          this.moveCamera(DIRECTION.LEFT, isShiftPressed);
        }
        if (this.keyboard.pressed("D") || this.mobileKeys.includes(KEYS.D)) {
          // move right
          this.moveCamera(DIRECTION.RIGHT, isShiftPressed);
        }
        if (this.keyboard.pressed("W") || this.mobileKeys.includes(KEYS.W)) {
          // move up
          this.moveCamera(DIRECTION.UP, isShiftPressed);
        }
        if (this.keyboard.pressed("S") || this.mobileKeys.includes(KEYS.S)) {
          // move down
          this.moveCamera(DIRECTION.DOWN, isShiftPressed);
          // Should map this to the scroll wheel event as well
        }
        if (this.keyboard.down("Z")) {
          this.toggleZoom();
        }
        // Disabled for 1.0 release
        // if (this.keyboard.down("X")) {
        //   this.invertSight();
        // }
        // Zero-out azimuth angle at current direction
        if (this.keyboard.down("0")) {
          this.zeroAzimuthAngle();
        }
        // Zero-out azimuth angle at 180° - 2A from current direction
        if (this.keyboard.down("2")) {
          this.zeroAzimuthAngleAt180Minus2A();
        }
        // Fire measurement laser
        if (this.keyboard.down("F")) {
          // Check intersections
          this.updateIntersections();
        }
        // Place Stake command key
        if (this.keyboard.down("P")) {
          this.pKeyAction();
        }
        // Prism Movement keys
        if (this.keyboard.pressed("I") || this.mobileKeys.includes(KEYS.I)) {
          // move this.prismPosition forward, relative to camera's direction
          getCameraHorizontalDirection(direction);
          this.prismPosition?.addScaledVector(direction, prismMoveSpeed);
        }
        if (this.keyboard.pressed("K") || this.mobileKeys.includes(KEYS.K)) {
          // move this.prismPosition backward, relative to camera's direction
          getCameraHorizontalDirection(direction);
          this.prismPosition?.addScaledVector(direction, -prismMoveSpeed);
        }
        if (this.keyboard.pressed("L") || this.mobileKeys.includes(KEYS.L)) {
          let left = new THREE.Vector3();
          getCameraHorizontalDirection(direction); // Forward direction
          left.crossVectors(camera.up, direction).normalize();
          this.prismPosition?.addScaledVector(left, -prismMoveSpeed);
        }
        if (this.keyboard.pressed("J") || this.mobileKeys.includes(KEYS.J)) {
          let left = new THREE.Vector3();
          getCameraHorizontalDirection(direction); // Forward direction
          left.crossVectors(camera.up, direction).normalize();
          this.prismPosition?.addScaledVector(left, prismMoveSpeed);
        }
      }
      // Animate Total Station Model
      if (
        models["total station"] &&
        this.status != STATUS.ROTATING_TO_INVERSE
      ) {
        const flipIfInverted = this.telescopeInverted ? Math.PI : 0;
        // Rotate Total Station Model with camera
        models["total station"].rotation.y =
          controls.azimuthAngle + Math.PI / 2 + flipIfInverted;
        // Update telescope vertical pitch
        this.updatePitch();
      }
      // Update angles displayed in the data output
      this.updateHorizontalAngle();
      this.updateVerticalAngle();

      // Animate reticle
      if (models["reticle"]) {
        // Rotate reticle group with camera
        models["reticle"].rotation.y = controls.azimuthAngle;
      }

      // render scene
      postprocessing.composer.render();

      // render labels after the scene
      this.labelRenderer.render(scene, camera);

      animationID = requestAnimationFrame(this.animate);
    },
    updateVerticalAngle: function () {
      this.verticalAngle = THREE.MathUtils.radToDeg(controls.polarAngle);
    },
    updateHorizontalAngle: function () {
      this.horizontalAngle =
        THREE.MathUtils.radToDeg(
          controls.azimuthAngle - this.azimuthAngleZero
        ) + this.horizontalAngleNoise;
    },
    addNoiseToHorizontalAngle: function () {
      const randomCoefficient = Math.random();
      // noise will be between -1 and 1 degrees per second
      this.horizontalAngleNoise =
        randomCoefficient * DEGREES_PER_SECOND * 2 - DEGREES_PER_SECOND;

      // call this function again in 0.5 +/- .3 seconds
      setTimeout(this.addNoiseToHorizontalAngle, 500 + randomCoefficient * 300);
    },
    pKeyAction: function () {
      // if prism is used, place stake in it's place
      if (this.prismPosition !== null) {
        if (!this.getHasMeasuredOnCurrentStep) {
          this.setErrorMessage(
            `You must make a measurement before your can place a stake.`
          );
          return;
        }

        const newStakePosition = this.prismPosition;
        // remove prism from scene
        this.prismPosition = null;
        // update stake position in store
        this.updateCurrentPointPosition(newStakePosition);
        this.addStake(newStakePosition, true);
        // TODO: if step requires nailing, animate/play sound of nail being hammered into stake
      }
    },
    updateDimensions: function () {
      // Bind renderer size to canvas
      // Source: https://r105.threejsfundamentals.org/threejs/lessons/threejs-responsive.html
      let { width, height, clientWidth, clientHeight } =
        this.renderer.domElement;
      let needsResize = width !== clientWidth || height !== clientHeight;

      if (!needsResize) {
        return;
      }

      this.renderer.setSize(clientWidth, clientHeight);
      // Update label renderer size
      if (this.labelRenderer) {
        this.labelRenderer.setSize(clientWidth, clientHeight);
      }
      //  Update camera aspect
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
    },
    addTarget: function (point) {
      // clone target and tripod for instantiation
      const tripod = models["target pole"].clone();
      const target = models["target"].clone();

      // Check for intersection with the reflector on the target
      intersectionTargets.push(target);
      // Use below if I want to check for intersection with only the reflector housing
      // targets.concat(
      //   target.children.filter(
      //     (child) => child.name == "reflector_housing" || child.name == "Circle"
      //   )
      // );
      // set heights
      target.position.y = POLE_HEIGHT + this.floorHeight;
      tripod.position.y = this.floorHeight;
      // Align rotations of two models
      // target.rotation.y -= Math.PI / 2;

      // Group target model and tripod together for placement on terrain
      const group = new THREE.Group();
      group.add(tripod);
      group.add(target);
      // Place at coordinates
      group.position.z = point.z;
      // Right-Left
      group.position.x = point.x;
      // Vertical position
      group.position.y = point.y;

      // Create label
      const labelDiv = createLabelDiv("Prism");
      const label = new CSS2DObject(labelDiv);
      label.position.set(0, -0.1, 0); // Position below stake
      group.add(label);

      scene.add(group);
      return group;
    },

    init: function () {
      // Check localStorage for stored azimuthAngleZero
      const storedAzimuthAngle = localStorage.getItem("azimuthAngleZero");
      if (storedAzimuthAngle !== null) {
        this.azimuthAngleZero = parseFloat(storedAzimuthAngle);
      }
      // Loader
      const loadingManager = new THREE.LoadingManager();
      loadingManager.onStart = () => {
        console.log("onStart");
      };
      loadingManager.onLoad = () => {
        console.log("onLoad");
      };
      loadingManager.onError = (err) => {
        console.log("onError", err);
      };

      this.clock = new THREE.Clock();
      this.keyboard = new KeyboardState();

      const canvas = this.$refs["threejs-canvas"];
      this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      configureRendererShadowSettings(this.renderer);
      // Camera
      camera = new THREE.PerspectiveCamera(
        45,
        canvas.clientWidth / canvas.clientHeight,
        0.5,
        1000
      );
      // Set Camera position
      camera.position.set(
        this.cameraDefaultPosition.x,
        this.cameraDefaultPosition.y,
        this.cameraDefaultPosition.z
      );
      // Initialize CSS2D renderer for labels
      this.labelRenderer = new CSS2DRenderer();
      this.labelRenderer.setSize(canvas.clientWidth, canvas.clientHeight);
      this.labelRenderer.domElement.style.position = "absolute";
      this.labelRenderer.domElement.style.top = "0";
      this.labelRenderer.domElement.style.pointerEvents = "none";
      canvas.parentElement.appendChild(this.labelRenderer.domElement);

      this.updateDimensions();

      // Set up plane for receiving shadows
      // ground plane width is twice the distance of the furthest point from the origin
      const planeWidth = CurveConstants.Ts * 1.2 * 2;
      const planeGeometry = new THREE.PlaneGeometry(planeWidth, planeWidth);
      planeGeometry.rotateX(-Math.PI / 2);

      const planeMaterial = new THREE.ShadowMaterial();
      planeMaterial.opacity;

      const plane = new THREE.Mesh(planeGeometry, planeMaterial);
      plane.position.y = this.floorHeight;
      plane.receiveShadow = true;
      scene.add(plane);

      // Cube Map background
      const hdrLoader = new THREE.TextureLoader();
      // Get the correct filename based on the device
      let filename;
      if (this.isMobile()) {
        filename = "panorama_mobile";
      } else {
        filename = "panorama_8k";
      }
      const getEnvMapPromise = (pointName) => {
        return (
          hdrLoader
            // load mobile envmap if on mobile device
            .loadAsync(`./environmentMaps/${pointName}/${filename}.jpg`)
            .then((image) => {
              image.mapping = THREE.EquirectangularReflectionMapping;
              environmentMaps[pointName] = image;
            })
        );
      };
      // Controls
      CameraControls.install({ THREE: THREE });

      controls = new CameraControls(camera, canvas);
      this.initCameraControls();

      /**
       * Raycaster
       */
      raycaster = new THREE.Raycaster();
      // don't count the sphere for intersections
      raycaster.far = 99;
      raycaster.near = Math.ceil(this.cameraDefaultPosition.z);
      // Directional Light for casting shadows
      const light = new THREE.DirectionalLight(0xfff5e6); // Warm white color
      // don't illuminate the scene too much, we only need this light to cast shadows
      light.intensity = 0.01;
      light.position.set(50, 100, 100);
      light.target.position.set(0, 0, 0);
      // cast shadow
      configureLightShadowSettings(light);
      scene.add(light);

      // Load Models
      const loader = new GLTFLoader();

      function loadModel(url) {
        return new Promise((resolve) => {
          loader.load(url, resolve);
        });
      }
      const promise1 = loadModel(
        this.getGLTFModelPath("total station tripod")
      ).then((gltf) => {
        models["tripod"] = gltf.scene;
        setCastShadow(models["tripod"]);
      });
      const promise2 = loadModel(this.getGLTFModelPath("total station")).then(
        (gltf) => {
          models["total station"] = gltf.scene;
          totalStation.armature =
            models["total station"].getObjectByName("TelescopeEmpty");
          // Start by looking through correct side of telescope
          totalStation.armature.rotation.z += Math.PI;
          models["total station"].rotation.y += Math.PI / 2;
          models["total station"].position.y = TRIPOD_HEIGHT;
          setCastShadow(models["total station"]);
        }
      );
      const promise3 = loadModel(this.getGLTFModelPath("tribrach")).then(
        (gltf) => {
          models["tribrach"] = gltf.scene;
          models["tribrach"].position.y = TRIPOD_HEIGHT - 0.015;
          models["tribrach"].scale.setScalar(1.1);
          setCastShadow(models["tribrach"]);
        }
      );

      const promise4 = loadModel(this.getGLBModelPath("pole")).then((gltf) => {
        models["target pole"] = gltf.scene;
        // rotate target pole on y axis 3/4 * PI radians
        models["target pole"].rotation.y += Math.PI * (3 / 4);
        setCastShadow(models["target pole"]);
      });

      const promise5 = loadModel(this.getGLBModelPath("prism")).then((gltf) => {
        gltf.scene.name = "target";
        models["target"] = gltf.scene;
        setCastShadow(models["target"]);
      });

      const promise6 = loadModel(this.getGLBModelPath("stake")).then((gltf) => {
        models["stake"] = gltf.scene;
        // double the thickness of the stake
        models["stake"].scale.set(2, 1, 2);
        setCastShadow(models["stake"]);
      });
      const promise7 = loadModel(this.getGLBModelPath("nailed stake")).then(
        (gltf) => {
          models["nailed stake"] = gltf.scene;
          // double the thickness of the stake
          models["nailed stake"].scale.set(2, 1, 2);
          setCastShadow(models["nailed stake"]);
        }
      );

      // Once all models have been loaded
      Promise.all([
        getEnvMapPromise(PointName.PI),
        getEnvMapPromise(PointName.TS),
        getEnvMapPromise(PointName.ST),
        getEnvMapPromise(PointName.SC),
        promise1,
        promise2,
        promise3,
        promise4,
        promise5,
        promise6,
        promise7
      ]).then(() => {
        //add origin models to the scene
        scene.add(models["tribrach"]);
        scene.add(models["total station"]);
        scene.add(models["tripod"]);

        // Instantiate targets
        // this.addTarget(this.getFromPointLocation);
        // this.addTarget(this.getToPointLocation);

        // Initialize existing stakes if the entry has been loaded from the datastore
        if (this.entryLoaded) {
          this.initStakes();
          this.initPrism();
        }

        if (this.isPanoramaDebugMode) {
          this.initGUI();
        }

        this.initEnvironmentMap();
        this.updateCameraPosition();

        //continue the process
        this.animate();
        // begin addNoiseToHorizontalAngle loop
        this.addNoiseToHorizontalAngle();

        this.loadingModels = false;
        this.$emit("modelsLoaded");
      });

      // Reticle - Provides a black background for the margins around the crosshair svg overlay
      models["reticle"] = new THREE.Mesh(
        new THREE.RingGeometry(0.03, 1, 64),
        new THREE.MeshBasicMaterial({ color: 0x000000, side: THREE.DoubleSide })
      );

      models["reticle"].position.set(0, this.cameraDefaultPosition.y, 0);
      models["reticle"].lookAt(camera.position);
      models["reticle"].visible = false;
      scene.add(models["reticle"]);

      // =-=-=-=-=-=-=-=-=-=-=-=-=-=-= post processing =-=-=-=-=-=-=-=-=-=-=-=-=-=-=
      const renderPass = new RenderPass(scene, camera);
      const composer = new EffectComposer(this.renderer);
      composer.addPass(renderPass);

      postprocessing.composer = composer;
    },
    // Sets up environment map for the current at point
    initEnvironmentMap: function () {
      const image = environmentMaps[this.getAtPointName];
      if (!image) return;
      // Dispose of existing environment map if it exists
      if (this.environmentMap) {
        this.environmentMap.geometry.dispose();
        this.environmentMap.material.dispose();
        scene.remove(this.environmentMap);
      }
      scene.background = image;
      scene.environment = image;
      // Ground-Projected Environment Map Initialization
      this.environmentMap = new GroundProjectedEnv(image, {
        center: this.getPointPositions[this.getAtPointName]
      });
      this.environmentMap.mapRotation =
        this.envParams[this.getAtPointName].rotation;
      this.environmentMap.height = this.envParams[this.getAtPointName].height;
      this.environmentMap.radius = this.envParams[this.getAtPointName].radius;
      this.environmentMap.intensity =
        this.envParams[this.getAtPointName].brightness;
      this.environmentMap.scale.setScalar(100);
      this.environmentMap.name = this.getAtPointName;
      scene.add(this.environmentMap);
    },
    initStakes: function () {
      console.log(this.getPointPositions);
      // initialize stakes from store
      Object.keys(this.getPointPositions)
        // filter out any points that are not set (null or undefined)
        .filter((pointName) => this.getPointPositions[pointName])
        .forEach((pointName) => {
          // do not animate stakes in the setup phase
          this.addStake(this.getPointPositions[pointName], false, pointName);
        });
    },
    addStake: function (point, animate = false, pointName = null) {
      const nailedStakes = [
        PointName.PI,
        PointName.TS,
        PointName.ST,
        PointName.SC,
        PointName.CS,
        PointName.MP
      ];
      console.log("addStake", pointName, "at point", point);
      // If not in a stake-out step or the setup phase, don't add a stake
      if (!pointName && !this.getCurrentStepId.startsWith("STAKE_OUT")) return;
      if (!pointName) {
        // get the name of the stake from the step id if no name is provided
        pointName = this.getCurrentStepId.replace("STAKE_OUT_", "");
      }
      const stakeModel = nailedStakes.includes(pointName)
        ? models["nailed stake"]
        : models["stake"];
      const stake = stakeModel.clone();
      // set the name of the stake so it can be removed from the scene later
      stake.name = pointName;
      const yAnimationOffset = animate ? 0.25 : 0;
      intersectionTargets.push(stake);
      stake.position.set(point.x, point.y + yAnimationOffset, point.z);
      if (animate) {
        // animate stake sinking into ground
        gsap.to(stake.position, {
          y: point.y,
          delay: 1,
          duration: 1,
          onStart: () => {
            scene.add(stake);
          }
        });
      } else {
        scene.add(stake);
      }

      // Create label
      const labelDiv = createLabelDiv(pointName);
      const label = new CSS2DObject(labelDiv);
      label.position.set(0, -0.1, 0); // Position below stake
      stake.add(label);
    },
    /**
     * Animate a stake lifting away, then remove it from the scene
     * @param {string} stakeId
     */
    removeStakeFromScene: function (stakeId) {
      const stake = scene.getObjectByName(stakeId);
      if (stake) {
        gsap.to(stake.position, {
          y: stake.position.y + 1,
          duration: 1,
          onComplete: () => {
            // Find and remove the CSS2DObject label - this is a hack to remove the label from the screen
            this.removeLabelFromObject(stake);
            stake.removeFromParent();
          }
        });
      }
    },
    removeLabelFromObject: function (object) {
      object.children.forEach((child) => {
        if (child instanceof CSS2DObject) {
          child.element.remove(); // Remove the DOM element
          child.removeFromParent(); // Remove from Three.js scene
        }
      });
    },
    addPrism: function (point, animate = false) {
      models["prism"] = this.addTarget(point);
      this.rotatePrismToFaceCamera();
      if (animate) {
        // TODO: animate prism sinking into ground
      }
    },
    rotatePrismToFaceCamera: function () {
      const prism = models["prism"];
      if (!prism) return;
      // Rotate towards total station
      prism.rotation.y = Math.atan2(
        camera.position.x - prism.position.x,
        camera.position.z - prism.position.z
      );
    },
    removePrism: function () {
      if (!models["prism"]) return;
      const prism = models["prism"];
      // animate prism lifting away
      gsap.to(prism.position, {
        y: prism.position.y + 1,
        duration: 1,
        onComplete: () => {
          if (prism) prism.visible = false;
          prism.removeFromParent();
          models["prism"] = null;
          // remove label from prism
          this.removeLabelFromObject(prism);
        }
      });
      // TODO: animate prism becoming transparent (low priority)
    },
    initPrism: function () {
      if (this.prismPosition) {
        this.addPrism(this.prismPosition);
      }
    },
    /**
     * Used for temporarily hiding points
     * @param {string} pointName - The name of the point to set the visibility of
     * @param {boolean} visible - Whether the point should be visible
     */
    setPointVisibility: function (pointName, visible) {
      const point = scene.getObjectByName(pointName);
      if (point) {
        point.visible = visible;
        // hide label associated with the point
        const label = point.children.find(
          (child) => child instanceof CSS2DObject
        );
        if (label) {
          label.visible = visible;
        }
      }
    }
  },
  watch: {
    prismPosition: {
      handler: function (newVal, oldVal) {
        if (this.loadingModels) return;

        const moveOrAddPrism = (position) => {
          if (models["prism"]) {
            // if prism exists in scene, update its position
            models["prism"].position.copy(position);
          } else {
            // if prism does not exist in scene, add it at the new position
            this.addPrism(position);
          }
        };
        // if new prism position is null and last prism position was not null, remove prism
        if (newVal === null) {
          this.removePrism();
          // if new prism position is not null and last prism position was null, add prism
        } else if (oldVal === null) {
          moveOrAddPrism(newVal);
          // if both new and old prism positions are not null, update prism position
        } else {
          moveOrAddPrism(newVal);
        }
      },
      deep: true
    },
    entryLoaded: function (newVal) {
      if (newVal) {
        // Check if models have been loaded
        if (!this.loadingModels) {
          this.initStakes();
          this.initPrism();
        } else {
          // If models are still loading, set a watcher to initialize after loading
          this.$watch("loadingModels", (loading) => {
            if (!loading) {
              this.initStakes();
              this.initPrism();
            }
          });
        }
      }
    },
    getAtPointName: function (newVal) {
      if (newVal) {
        // move camera to the new at point
        this.updateCameraPosition();
        // update environment map to the new at point
        this.initEnvironmentMap();
      }
    }
  }
};
</script>

<style scoped>
.crosshair-overlay {
  pointer-events: none;
}
</style>
